import { Link } from 'react-router-dom';
import React from 'react';
import Header from './shared/Header';
import Footer from './shared/Footer';
import {
  ArrowRight,
  ChevronRight,
  Bot,
  BookOpen,
  GitBranch,
  Database,
  Search,
  Code2,
  Activity,
} from 'lucide-react';

const LandingPage = () => {
  const services = [
    {
      icon: <Bot size={24} />,
      title: 'Claude Implementation & Integration',
      anchor: '/services#claude',
    },
    {
      icon: <BookOpen size={24} />,
      title: 'RAG & Knowledge Systems',
      anchor: '/services#rag',
    },
    {
      icon: <GitBranch size={24} />,
      title: 'Agentic Workflows & Automation',
      anchor: '/services#agents',
    },
    {
      icon: <Database size={24} />,
      title: 'Data Infrastructure & Analytics',
      anchor: '/services#data',
    },
  ];

  const howWeWork = [
    {
      step: '01',
      icon: <Search size={22} />,
      title: 'Discover',
      description: 'A scoped discovery sprint to understand the problem, the constraints, and the right shape of solution.',
    },
    {
      step: '02',
      icon: <Code2 size={22} />,
      title: 'Build',
      description: 'We ship working software, not recommendations. Every engagement ends with something in production.',
    },
    {
      step: '03',
      icon: <Activity size={22} />,
      title: 'Operate',
      description: 'Hand off cleanly or stay on retainer to evolve the system as your needs grow.',
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <Header />

      {/* Hero */}
      <section className="pt-20 md:pt-32 pb-12 md:pb-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-white" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="absolute top-0 right-[10%] w-20 h-20 bg-blue-50 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-[20%] w-32 h-32 bg-purple-50 rounded-full blur-2xl pointer-events-none" />

          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
              AI & Data Delivery Studio
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Ship agentic AI solutions without the consulting bloat
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl">
              We're a boutique studio of senior engineers, architects, and product designers who build,
              integrate, and operate AI systems for companies that need results.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium
                           transition-all shadow-lg hover:shadow-xl hover:translate-y-[-1px] flex items-center"
              >
                Start a Project
                <ArrowRight className="ml-2" size={18} />
              </Link>
              <Link
                to="/work"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-6 py-3 border border-gray-200 hover:border-blue-500 hover:text-blue-600
                           text-gray-700 rounded-xl font-medium transition-all flex items-center"
              >
                See Our Work
                <ChevronRight className="ml-1" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services overview strip */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.anchor}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100
                           hover:shadow-md hover:border-blue-100 transition-all group"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4
                                text-blue-600 group-hover:bg-blue-100 transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 leading-snug">
                  {service.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">How we work</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              A repeatable delivery model built around shipping, not slide decks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {howWeWork.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="text-blue-500 font-medium mb-4">{item.step}</div>
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof: video demo with studio framing */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">We ship real products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              DataBackfill Sync is our own GA4 → BigQuery product. Built in-house, still running,
              serving hundreds of properties. Here's how we shipped it.
            </p>
          </div>
          <div className="rounded-2xl bg-white shadow-2xl overflow-hidden max-w-4xl mx-auto">
            <div className="aspect-video">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/RCo42fiLoS0?si=1fz_e3MeRp2LLQ_u"
                title="DataBackfill Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team teaser */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-3xl p-8 md:p-14 text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              A boutique core team backed by a network of senior practitioners.
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Built to ship.
            </p>
            <Link
              to="/team"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
            >
              Learn more
              <ChevronRight className="ml-1" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-600 rounded-3xl p-6 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full transform translate-x-1/2 -translate-y-1/2 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-700 rounded-full transform -translate-x-1/2 translate-y-1/2 blur-2xl" />
            <div className="relative max-w-lg mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Ready to build something real?
              </h2>
              <Link
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-white text-blue-600
                           rounded-xl text-base md:text-lg font-semibold hover:bg-blue-50 transition-all
                           shadow-lg hover:shadow-xl hover:translate-y-[-1px]"
              >
                Start a Project
                <ChevronRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
